import React, { useState } from 'react';
import {
  Paper, Box, Typography, IconButton, Tooltip, Chip, Badge,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  SmartToy as AIIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  Transform as TransformIcon,
  CallSplit as ConditionalIcon,
  Loop as LoopIcon,
  Webhook as WebhookIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Storage as DatabaseIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Notes as NotesIcon,
  BugReport as BugIcon,
  Description as DescriptionIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';

function FlowNode({
  node, onSelect, isSelected, isConnectionStart, onDelete, selectedMode, onDrag, onNodeConfigUpdate, updateNode, hasBreakpoint = false,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Debug logging for logger/debugger nodes (commented out to reduce console clutter)
  // console.log('🎨 FlowNode rendering:', node.id, node.type, node.data?.label);

  // Enhanced node color scheme
  const getNodeColor = (type) => {
    const colors = {
      start: '#4CAF50',
      end: '#F44336',
      'ai-agent': '#4CAF50',
      'code-executor': '#2196F3',
      'api-call': '#FF9800',
      webhook: '#3F51B5',
      email: '#F44336',
      sms: '#4CAF50',
      conditional: '#F44336',
      loop: '#607D8B',
      transform: '#9C27B0',
      database: '#795548',
      delay: '#607D8B',
      scheduler: '#3F51B5',
      form: '#1976d2',
      rag: '#00BCD4',
      'memory-store': '#8BC34A',
      'mcp-tool': '#9C27B0',
      'human-input': '#FF5722',
      'file-operations': '#795548',
      'data-validation': '#E91E63',
      calendar: '#E91E63',
      logger: '#FF9800',
      debugger: '#F44336',
      'web-crawler': '#795548',
      connector: '#9C27B0',
      policy: '#2e7d32',
      'execute-flow': '#1976d2',
    };
    return colors[type] || '#757575';
  };

  // Enhanced node icon mapping
  const getNodeIcon = (type) => {
    const icons = {
      start: <StartIcon />,
      end: <StopIcon />,
      'ai-agent': <AIIcon />,
      'code-executor': <CodeIcon />,
      'api-call': <ApiIcon />,
      webhook: <WebhookIcon />,
      email: <EmailIcon />,
      sms: <SmsIcon />,
      conditional: <ConditionalIcon />,
      loop: <LoopIcon />,
      transform: <TransformIcon />,
      database: <DatabaseIcon />,
      delay: <PauseIcon />,
      scheduler: <ScheduleIcon />,
      form: <TransformIcon />,
      rag: <DatabaseIcon />,
      'memory-store': <DatabaseIcon />,
      'mcp-tool': <StorageIcon />,
      'human-input': <AIIcon />,
      'file-operations': <StorageIcon />,
      'data-validation': <TransformIcon />,
      calendar: <ScheduleIcon />,
      logger: <NotesIcon />,
      debugger: <BugIcon />,
      'web-crawler': <ApiIcon />,
      connector: <ApiIcon />,
      policy: <DescriptionIcon />,
      'execute-flow': <AccountTreeIcon />,
    };
    return icons[type] || <div />;
  };

  // Enhanced mouse handling
  const handleMouseDown = (e) => {
    // console.log('Node mouse down:', node.id, selectedMode);
    if (selectedMode === 'connect') return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    // console.log('Starting drag for node:', node.id);

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - e.clientX;
      const deltaY = moveEvent.clientY - e.clientY;

      const newPosition = {
        x: Math.max(0, node.position.x + deltaX),
        y: Math.max(0, node.position.y + deltaY),
      };

      // console.log('Dragging node:', node.id, 'to position:', newPosition);
      onDrag && onDrag(node.id, newPosition);
    };

    const handleMouseUp = () => {
      // console.log('Ending drag for node:', node.id);
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleNodeClick = (e) => {
    // Node click handling - debugging commented out to reduce console clutter
    // console.log('=== FLOWNODE CLICK EVENT ===');
    // console.log('Node ID:', node.id);
    // console.log('Event type:', e.type);
    // console.log('Event target:', e.target);
    // console.log('Is dragging:', isDragging);
    // console.log('Selected mode:', selectedMode);
    // console.log('Event current target:', e.currentTarget);
    // console.log('===========================');

    e.stopPropagation();

    // Don't handle click if we're dragging
    if (isDragging) {
      // console.log('Ignoring click - dragging in progress');
      return;
    }

    // Handle connection mode
    if (selectedMode === 'connect') {
      // console.log('Connection mode click on node:', node.id);
      onSelect(node, e, false, 'connect');
      return;
    }

    // Handle normal selection
    // console.log('Normal click on node:', node.id);
    onSelect(node, e, false, 'select');
  };

  const handleDoubleClick = (e) => {
    // Node double-click handling - debugging commented out to reduce console clutter
    // console.log('=== FLOWNODE DOUBLE CLICK EVENT ===');
    // console.log('Node ID:', node.id);
    // console.log('Event type:', e.type);
    // console.log('Is dragging:', isDragging);
    // console.log('Selected mode:', selectedMode);
    // console.log('===========================');

    e.stopPropagation();

    // Don't handle double-click if we're dragging or in connection mode
    if (isDragging || selectedMode === 'connect') {
      // console.log('Ignoring double-click - dragging or connection mode');
      return;
    }

    // console.log('Opening config for node:', node.id);
    onSelect(node, e, true, 'config');
  };

  const handleConnectionPointClick = (e) => {
    // console.log('Connection point click:', node.id);
    e.stopPropagation();
    if (selectedMode === 'connect') {
      onSelect(node, e, false, 'connect');
    }
  };

  const nodeColor = getNodeColor(node.type);
  const nodeIcon = getNodeIcon(node.type);

  // console.log('🎨 Node type:', node.type, 'Color:', nodeColor, 'Icon:', nodeIcon ? '✅' : '❌');

  // Enhanced node labeling - show only label when available, type only when no label
  const getEnhancedNodeLabel = () => {
    const customName = node.data?.label || node.label;
    const nodeType = node.type;

    // If there's a custom name, show only the custom name
    if (customName && customName !== nodeType) {
      return {
        primary: customName,
        secondary: null, // Don't show type as subtitle anymore
        hasCustomName: true,
      };
    }

    // Otherwise just show the type
    return {
      primary: nodeType,
      secondary: null,
      hasCustomName: false,
    };
  };

  const nodeLabel = getEnhancedNodeLabel();

  return (
    <Paper
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        p: 2, // Increased padding for bigger nodes
        cursor: selectedMode === 'connect' ? 'crosshair' : isDragging ? 'grabbing' : 'grab',
        border: isSelected ? 3 : isConnectionStart ? 3 : 2,
        borderColor: isConnectionStart ? '#ff9800' : isSelected ? '#1976d2' : nodeColor,
        backgroundColor: `${nodeColor}08`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        boxShadow: isSelected ? 8 : isConnectionStart ? 6 : 3,
        '&:hover': {
          boxShadow: isSelected ? 12 : 8,
          borderColor: selectedMode === 'connect' ? '#ff9800' : '#1976d2',
          transform: 'translateY(-2px)',
        },
        zIndex: isDragging ? 10 : isConnectionStart ? 5 : isSelected ? 3 : 2,
        opacity: isDragging ? 0.9 : 1,
        transform: isDragging ? 'scale(1.05) rotate(2deg)' : 'scale(1)',
        borderRadius: 3,
        overflow: 'hidden',
        minHeight: '140px', // Increased minimum height
        maxHeight: '160px', // Increased maximum height
        // border: '2px dashed #f0f', // Uncomment for debug
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        // Direct click on Paper element - debugging commented out to reduce console clutter
        // console.log('=== DIRECT PAPER CLICK ===');
        // console.log('Direct click on Paper element');
        // console.log('Node ID:', node.id);
        // console.log('Event:', e);
        // console.log('========================');
        handleNodeClick(e);
      }}
      onDoubleClick={(e) => {
        // Direct double-click on Paper element - debugging commented out to reduce console clutter
        // console.log('=== DIRECT PAPER DOUBLE CLICK ===');
        // console.log('Direct double-click on Paper element');
        // console.log('Node ID:', node.id);
        // console.log('Event:', e);
        // console.log('==========================');
        handleDoubleClick(e);
      }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Breakpoint indicator */}
      {hasBreakpoint && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: '#f44336',
            border: '2px solid white',
            boxShadow: 1,
            zIndex: 5,
          }}
          title="Breakpoint"
        />
      )}
      {/* Enhanced Node Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          mb: 1,
        }}
      >
        {/* Node Type Badge with Execution Order */}
        <Badge
          badgeContent={node.data?.stateVars?.length || 0}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.7rem',
              height: '18px',
              minWidth: '18px',
              backgroundColor: node.data?.stateVars?.length > 0 ? '#4CAF50' : 'transparent',
              color: 'white',
              fontWeight: 600,
            },
          }}
        >
          <Chip
            icon={nodeIcon}
            label={node.type}
            size="small"
            sx={{
              backgroundColor: nodeColor,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '24px',
              '& .MuiChip-icon': {
                color: 'white',
                fontSize: '1rem',
              },
            }}
          />
        </Badge>

        {/* Action Buttons */}
        {showActions && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Configure Node">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(node, e, true, 'config');
                }}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: nodeColor,
                  width: 24,
                  height: 24,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,1)',
                  },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Node">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(node.id);
                }}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: '#f44336',
                  width: 24,
                  height: 24,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,1)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Enhanced Node Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 0, // Allow flex shrinking
          overflow: 'hidden',
        }}
      >
        {/* Node Label */}
        <Box sx={{ textAlign: 'center', mb: 1, width: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.85rem',
              lineHeight: 1.2,
              maxWidth: '100%',
              wordBreak: 'break-word',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: '1.2rem',
            }}
          >
            {nodeLabel.primary}
          </Typography>

          {/* Show node type as subtitle if there's a custom name */}
          {nodeLabel.secondary && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontStyle: 'italic',
                display: 'block',
                mt: 0.5,
              }}
            >
              {nodeLabel.secondary}
            </Typography>
          )}

          {/* Visual indicator for custom named nodes */}
          {nodeLabel.hasCustomName && (
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: '#4CAF50',
                margin: '2px auto 0',
              }}
            />
          )}
        </Box>

        {/* Node Status Indicator */}
        {node.data?.status && (
          <Chip
            label={node.data.status}
            size="small"
            sx={{
              backgroundColor: node.data.status === 'active' ? '#4CAF50' : '#FF9800',
              color: 'white',
              fontSize: '0.7rem',
              height: '20px',
              mb: 1,
            }}
          />
        )}

        {/* Connection Points */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            mt: 1,
          }}
        >
          {/* Input Connection Point */}
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: nodeColor,
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              cursor: selectedMode === 'connect' ? 'crosshair' : 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.2)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.4)',
              },
            }}
            onClick={handleConnectionPointClick}
          />

          {/* Output Connection Point */}
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: nodeColor,
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              cursor: selectedMode === 'connect' ? 'crosshair' : 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.2)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.4)',
              },
            }}
            onClick={handleConnectionPointClick}
          />
        </Box>
      </Box>

      {/* Enhanced State Variables Section */}
      {node.data?.stateVars && node.data.stateVars.length > 0 && (
        <Box
          sx={{
            mt: 0.5,
            pt: 0.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            width: '100%',
          }}
        >
          {/* State Variables Summary */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
          >
            <Chip
              icon={<StorageIcon sx={{ fontSize: '0.7rem' }} />}
              label={
                node.data.stateVars.length <= 3
                  ? `${node.data.stateVars.length} var${node.data.stateVars.length !== 1 ? 's' : ''}`
                  : `${node.data.stateVars.length} vars`
              }
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6rem',
                height: '18px',
                borderColor: nodeColor,
                color: nodeColor,
                backgroundColor: `${nodeColor}10`,
                '& .MuiChip-icon': {
                  color: nodeColor,
                  fontSize: '0.7rem',
                },
                '& .MuiChip-label': {
                  px: 0.5,
                  fontSize: '0.6rem',
                  fontWeight: 500,
                },
              }}
            />

            {/* Quick preview of first few variables on hover */}
            <Tooltip
              title={(
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    State Variables (
                    {node.data.stateVars.length}
                    ):
                  </Typography>
                  {node.data.stateVars.slice(0, 5).map((stateVar, index) => (
                    <Typography key={index} variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                      •
                      {' '}
                      {stateVar}
                    </Typography>
                  ))}
                  {node.data.stateVars.length > 5 && (
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', fontStyle: 'italic', mt: 0.5 }}>
                      ... and
                      {' '}
                      {node.data.stateVars.length - 5}
                      {' '}
                      more
                    </Typography>
                  )}
                </Box>
              )}
              placement="top"
              arrow
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: `${nodeColor}20`,
                  border: `1px solid ${nodeColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'help',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.55rem',
                    fontWeight: 600,
                    color: nodeColor,
                    lineHeight: 1,
                  }}
                >
                  ?
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default FlowNode;
